import type { FC, MouseEvent } from 'react';
import { useState, useMemo, useEffect } from 'react';
import { Box, Paper, Table, TableContainer } from '@mui/material';
import { TaskRo } from 'src/app/services/api.generated';
import { CustomTablePagination } from '../../../molecules/CustomTablePagination';
import { TasksTableHeader } from './TasksTableHeader';
import { TasksTableBody } from './TasksTableBody';
import { TaskTableToolbar } from './TaskTableToolbar';
import { tableOrderType } from 'src/types/generalTypes';
import { useTranslation } from 'react-i18next';
import { EmptyList } from 'src/components/molecules/EmptyList';
import { WorkspaceFolderQueryKey } from 'src/utils/taskProgressStreamUtils';
import { useAppSelector } from 'src/app/reduxCustomHooks';

type TasksTablePropsType = {
  data: TaskRo[];
  isLoading: boolean;
  pagination?: boolean;
  showToolbar?: boolean;
  showCheckbox?: boolean;
  folderQueryKey?: WorkspaceFolderQueryKey;
  goToCreateTaskPage?: () => void;
  hideCreateButton?: boolean;
  emptyText?: string;
  /** When true, skip client-side name/assignee filtering (server already filtered). */
  disableClientFilter?: boolean;
  /** Skip outer bordered Paper when parent already provides the shell. */
  embedded?: boolean;
  searchInput?: string;
  setSearchInput?: React.Dispatch<React.SetStateAction<string>>;
  ocrSearchInput?: string;
  setOcrSearchInput?: React.Dispatch<React.SetStateAction<string>>;
  ocrSearchPlaceholder?: string;
  assignedToMeOnly?: boolean;
  setAssignedToMeOnly?: React.Dispatch<React.SetStateAction<boolean>>;
};

export const TasksTable: FC<TasksTablePropsType> = ({
  data,
  isLoading,
  pagination = true,
  showToolbar = true,
  showCheckbox = true,
  folderQueryKey,
  goToCreateTaskPage,
  hideCreateButton = false,
  emptyText,
  disableClientFilter = false,
  embedded = false,
  searchInput: controlledSearchInput,
  setSearchInput: controlledSetSearchInput,
  ocrSearchInput,
  setOcrSearchInput,
  ocrSearchPlaceholder,
  assignedToMeOnly: controlledAssignedToMeOnly,
  setAssignedToMeOnly: controlledSetAssignedToMeOnly,
}) => {
  const [order, setOrder] = useState<tableOrderType>('desc');
  const [orderBy, setOrderBy] = useState<keyof TaskRo>('createdAt');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [selected, setSelected] = useState<string[]>([]);
  const [internalSearchInput, setInternalSearchInput] = useState('');
  const [internalAssignedToMeOnly, setInternalAssignedToMeOnly] =
    useState(false);

  const searchInput = controlledSearchInput ?? internalSearchInput;
  const setSearchInput = controlledSetSearchInput ?? setInternalSearchInput;
  const assignedToMeOnly =
    controlledAssignedToMeOnly ?? internalAssignedToMeOnly;
  const setAssignedToMeOnly =
    controlledSetAssignedToMeOnly ?? setInternalAssignedToMeOnly;

  useEffect(() => {
    setPage(0);
  }, [searchInput, assignedToMeOnly, ocrSearchInput]);

  const { t } = useTranslation();
  const currentUsername = useAppSelector((state) => state.auth.user?.username);

  const filteredData = useMemo(() => {
    if (disableClientFilter) {
      return data;
    }
    let result = data;
    if (assignedToMeOnly && currentUsername) {
      result = result.filter(({ assigneeId }) => assigneeId === currentUsername);
    }
    if (!searchInput) return result;
    const needle = searchInput.toLowerCase();
    return result.filter(({ name }) => name.toLowerCase().includes(needle));
  }, [
    data,
    searchInput,
    assignedToMeOnly,
    currentUsername,
    disableClientFilter,
  ]);

  const handleRequestSort = (
    _: MouseEvent<unknown>,
    property: keyof TaskRo
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const toolbar = showToolbar ? (
    <TaskTableToolbar
      rows={data}
      selected={selected}
      setSelected={setSelected}
      searchInput={searchInput}
      setSearchInput={setSearchInput}
      folderQueryKey={folderQueryKey}
      goToCreateTaskPage={goToCreateTaskPage}
      assignedToMeOnly={assignedToMeOnly}
      setAssignedToMeOnly={setAssignedToMeOnly}
      hideCreateButton={hideCreateButton}
      ocrSearchInput={ocrSearchInput}
      setOcrSearchInput={setOcrSearchInput}
      ocrSearchPlaceholder={ocrSearchPlaceholder}
      flush={embedded}
    />
  ) : null;

  if (!isLoading && !data.length) {
    return (
      <>
        {toolbar}
        <EmptyList text={emptyText || t('tasksListIsEmpty')} />
      </>
    );
  }

  const tableContent = (
    <>
      {toolbar}
      <TableContainer>
        <Table size="small">
          <TasksTableHeader
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rows={filteredData}
            selected={selected}
            setSelected={setSelected}
            showCheckbox={showCheckbox}
          />
          <TasksTableBody
            isLoading={isLoading}
            rows={filteredData}
            order={order}
            orderBy={orderBy}
            page={page}
            rowsPerPage={rowsPerPage}
            selected={selected}
            setSelected={setSelected}
            showCheckbox={showCheckbox}
            folderQueryKey={folderQueryKey}
          />
        </Table>
      </TableContainer>
      {pagination && (
        <CustomTablePagination
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          handleChangePage={handleChangePage}
          rowsLength={filteredData.length}
          colSpan={8}
          rowsPerPageOptions={[20, 50, 100]}
        />
      )}
    </>
  );

  if (embedded) {
    return <Box sx={{ overflow: 'hidden' }}>{tableContent}</Box>;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        overflow: 'hidden',
        border: (theme) => `1px solid ${theme.palette.hairline}`,
      }}
    >
      {tableContent}
    </Paper>
  );
};
